import { Error } from '@material-ui/icons';
import {
  ColorDynamic,
  Column,
  Columns,
  Inline,
  Tag,
  useResponsiveValue,
} from '@superdispatch/ui';
import { SidebarContainer, SidebarMenuItem } from '@superdispatch/ui-lab';
import { Sidebar } from 'shared/layout/Sidebar';
import { ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { openExternalURL } from 'shared/helpers/URLHelpers';
import { useSubscriptionPaymentSeverity } from 'shared/layout/notification-stack/data/NotificationAPI';
import { useShowAddBillingAddressDialog } from 'shared/modules/subscription/core/useShowAddBillingAddressDialog';
import { SidebarMenuItemLink } from 'shared/routing/SidebarMenuItemLink';
import { useEdiSettingsAccess } from './data/useEdiSettingsAccess';
import { WarningTriangleIcon } from 'shared/icons/WarningTriangleIcon';

interface SettingsSidebarProps {
  href: string;
  children?: ReactNode;
}

function SettingsSidebarExternalItem({ href, children }: SettingsSidebarProps) {
  return (
    <SidebarMenuItem
      external={true}
      onClick={() => {
        openExternalURL(href, { target: '_blank' });
      }}
    >
      {children}
    </SidebarMenuItem>
  );
}

export function SettingsSidebar() {
  const { doesBillingAddressNotExist } = useShowAddBillingAddressDialog();
  const { isVisible: isEdiSettingsVisible } = useEdiSettingsAccess();
  const subscriptionSeverity = useSubscriptionPaymentSeverity();

  return (
    <Sidebar title="Settings">
      <SidebarMenuItemLink to="/settings/general/">General</SidebarMenuItemLink>
      <SidebarMenuItemLink to="/settings/terminals/">
        Terminals
      </SidebarMenuItemLink>
      <SidebarMenuItemLink to="/settings/driver-app/">
        Driver App
      </SidebarMenuItemLink>
      <SidebarMenuItemLink to="/settings/billing/">Billing</SidebarMenuItemLink>
      <SidebarMenuItemLink to="/settings/factoring/">
        Factoring
      </SidebarMenuItemLink>
      <SidebarMenuItemLink to="/settings/quickbooks/">
        Quickbooks Integration
      </SidebarMenuItemLink>
      {isEdiSettingsVisible && (
        <SidebarMenuItemLink to="/settings/edi/">
          OEM EDI Integration
        </SidebarMenuItemLink>
      )}
      <SidebarMenuItemLink to="/settings/subscription/">
        <Columns space="xlarge" align="center">
          <Column width="fluid">Subscription Details</Column>
          <Column width="content">
            <Inline space="xsmall" verticalAlign="center">
              {doesBillingAddressNotExist && (
                <Tag variant="subtle" color="yellow">
                  To Do
                </Tag>
              )}
              {subscriptionSeverity === 'critical' && (
                <Error
                  fontSize="small"
                  htmlColor={ColorDynamic.Red500}
                  titleAccess="Action required"
                />
              )}
              {subscriptionSeverity === 'caution' && (
                <WarningTriangleIcon
                  fontSize="small"
                  htmlColor={ColorDynamic.Yellow500}
                  titleAccess="Attention needed"
                />
              )}
            </Inline>
          </Column>
        </Columns>
      </SidebarMenuItemLink>
      <SidebarMenuItemLink to="/settings/notifications/">
        Notifications
      </SidebarMenuItemLink>
      <SettingsSidebarExternalItem href="/loadboard/settings/notifications/">
        Loadboard Notifications
      </SettingsSidebarExternalItem>
    </Sidebar>
  );
}

export function SettingsContainerOutlet() {
  const isMobile = useResponsiveValue(true, true, false);

  if (isMobile) {
    return <Outlet />;
  }

  return (
    <SidebarContainer sidebar={<SettingsSidebar />}>
      <Outlet />
    </SidebarContainer>
  );
}
